import React, { useCallback, useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
// ---- Import hooks
import useMakeRequest from "../../../../hooks/useMakeRequest";
import pixelEvent from "../../../../hooks/pixelEvent";
// ---- Import componets
import Preloader from "../../../../components/UI/Preloader/Preloader";
import Input from "../../../../components/UI/Input/Input";
// ---- Import images
import iconPencil from "../../../../assets/images/profile/icon_pencil.svg";

import "./ProfileDataItem.sass";
import ProfileSelect from "../../../../pagesComponents/profile/ProfileSelect/ProfileSelect";

function ProfileDataItem({ userData, fieldItem }) {
  const [editMode, setEditMode] = useState(false);
  const [currentField, setCurrentField] = useState(null);

  const [textFieldValue, setTextFieldValue] = useState(fieldItem.data || "");
  const [selectedOption, setSelectedOption] = useState(
    fieldItem.options?.find((o) => o.value === fieldItem.data) || null
  );

  const [error, setError] = useState("");
  const [setParams, apiData, isLoading, hasError, apiError] = useMakeRequest();

  const validate = useCallback(
    (val) => {
      const trimmed = typeof val === "string" ? val.trim() : val;

      const regName =
        /^(?!.*['-]{2})(?!.* -|-.)(?!.*['-]$)(?!.*^['-])[a-z' -]+$/i;
      // eslint-disable-next-line no-useless-escape
      const regPhone = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/g;
      const regEmail =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const regAddress =
        /^(?!.*['`,-]{2})(?!.* -|-.)(?!.*['`,-]$)(?!.*^['`,-])(?=.*[a-z])[a-z0-9,.'`/ -]+(?!.*([,.'`/])\1)(?!.*(?:[^a-z\d]\.|,\.|\/))(?!.*[^a-z\\d]\/|\/[^a-z\\d])[a-z0-9,.'`/ -]+$/i;
      // eslint-disable-next-line default-case

      switch (fieldItem.title) {
        case "Full name":
          if (!trimmed) return "Field required";
          if (!regName.test(trimmed)) return "Invalid name";
          if (trimmed.length > 50) return "Not supported length of the field";
          return "";

        case "Email":
          if (!regEmail.test(String(trimmed).toLowerCase()))
            return "Invalid email";
          if (trimmed.length > 50) return "Not supported length";
          return "";

        case "Primary phone":
          if (!regPhone.test(val)) return "Invalid phone";
          if (trimmed.length > 17)
            return "Phone number can't be longer than 17 digits";
          return "";

        case "Address":
          if (!trimmed) return "";
          if (!regAddress.test(trimmed)) return "Invalid address";
          if (trimmed.length > 100) return "Not supported length of the field";
          return "";

        default:
          return "";
      }
    },
    [fieldItem.title]
  );

  const save = useCallback(
    (opt = null) => {
      if (!currentField) return;

      const newValue =
        currentField.type === "select"
          ? opt?.value ?? selectedOption?.value ?? ""
          : textFieldValue.trim();

      const validationError = validate(newValue);
      if (validationError) {
        setError(validationError);
        return;
      }

      setParams({
        c: "accountStore",
        a: "setAccountAttribute",
        p: { [currentField.field]: newValue },
      });
    },
    [currentField, selectedOption, textFieldValue, validate, setParams]
  );

  const startEdit = () => {
    setEditMode(true);
    setCurrentField(fieldItem);

    pixelEvent({
      evt_src: "acct",
      evt_action: `edit_${fieldItem.field}`,
      guid: userData.guid,
    });
  };

  useEffect(() => {
    if (!apiData) return;

    if (!hasError && !apiError) {
      const dataResponse = Object.values(apiData)[0];

      if (currentField.type === "select") {
        setSelectedOption(
          fieldItem?.options?.find((o) => o.value === dataResponse)
        );
      } else {
        setTextFieldValue(dataResponse);
      }

      setEditMode(false);
      setError("");

      const pixelParam = {
        evt_src: "reg",
        evt_action: "partial",
        userid: userData.cid,
      };

      if (currentField.field === "fullName") {
        const name = dataResponse.split(" ");
        pixelParam.firstName = name[0] || "";
        name.splice(0, 1);
        pixelParam.lastName = name.join(" ") || "";
      }
      if (currentField.field === "email") pixelParam.email = dataResponse;
      if (currentField.field === "phone")
        pixelParam.phoneNumber = encodeURIComponent(dataResponse);
      if (currentField.field === "address")
        pixelParam.address = encodeURIComponent(dataResponse);
      if (currentField.field === "age_status")
        pixelParam.age_status = dataResponse;
      if (currentField.field === "gender") pixelParam.gender = dataResponse;

      pixelEvent(pixelParam);
    } else {
      setError(apiError);
    }
  }, [apiData, apiError, hasError]);

  useEffect(() => {
    if (currentField?.type === "select" && selectedOption) {
      save();
    }
  }, [selectedOption]);

  return (
    <>
      <section className="profile-data-cell">
        <h5 className="profile-data-cell__title">{fieldItem.title}</h5>
        {fieldItem?.type === "select" ? (
          <div className="profile-data-cell-value">
            <ProfileSelect
              value={selectedOption?.value}
              options={fieldItem?.options}
              onChange={(opt) => {
                setSelectedOption(opt);
                setCurrentField(fieldItem);
                setError("");
              }}
              onOpen={() => startEdit()}
              className="profile-data-cell-value__field"
              errorMsg={error}
            />
            {isLoading && (
              <Preloader
                width="15px"
                className="profile-data-cell-value__preloader"
              />
            )}
          </div>
        ) : (
          <>
            {editMode ? (
              <div className="profile-data-cell-value">
                <Input
                  className="profile-data-cell-value__field"
                  value={textFieldValue}
                  onChange={(e) => {
                    setTextFieldValue(e.target.value);
                    setError("");
                  }}
                  onBlur={() => save()}
                  error={error}
                  autofocus
                />
                {isLoading && (
                  <Preloader
                    width="15px"
                    className="profile-data-cell-value__preloader"
                  />
                )}
              </div>
            ) : (
              <div
                className={`profile-data-cell-value ${
                  fieldItem.isEditable ? "editable" : ""
                }`}
                role="presentation"
                onClick={() => {
                  if (!fieldItem.isEditable) return;
                  startEdit();
                }}
              >
                <span className="profile-data-cell-value__text">
                  {textFieldValue}
                </span>
                <span className="profile-data-cell-value__icon">
                  {fieldItem?.type === "text" && (
                    <img src={iconPencil} alt="edit-icon" />
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

ProfileDataItem.propTypes = {
  fieldItem: PropTypes.shape({
    field: PropTypes.string,
    title: PropTypes.string,
    data: PropTypes.string,
    isEditable: PropTypes.bool,
    type: PropTypes.oneOf(["text", "select"]).isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
  }),
  userData: PropTypes.objectOf(PropTypes.string),
};

ProfileDataItem.defaultProps = {
  userData: null,
  fieldItem: null,
};

const mapStateToProps = (state) => ({
  userData: state.userDataStore.userData,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileDataItem);
